// frontend/src/pages/LandingPage.js - Landing page kept in sync with the mobile app
import React, { useState, useEffect } from 'react';
import { TrendingUp, Star, Shield, Sparkles, ArrowRight, Play, X, Home, Trophy, User } from 'lucide-react';

const PURPLE = '#7c3aed';
const BLUE = '#2563eb';

// ---------------------------------------------------------------------------
// Phone mockup - mirrors the real mobile dashboard (NB A props, ₦ balance,
// Safety Play / Beast Mode, Bet Copilot tab). Keep this in step with
// mobile/src/screens/DashboardScreen.js so the marketing shot stays honest.
// ---------------------------------------------------------------------------
const PhoneMockup = () => (
  <div style={{
    width: '280px',
    height: '560px',
    background: 'linear-gradient(145deg, #1f2937, #111827)',
    borderRadius: '36px',
    border: '4px solid #374151',
    boxShadow: '0 25px 80px rgba(0,0,0,0.4), 0 0 60px rgba(124,58,237,0.25)',
    overflow: 'hidden',
    position: 'relative'
  }}>
    <div style={{ height: '100%', background: '#000', display: 'flex', flexDirection: 'column' }}>
      {/* Header - logo, greeting, balance pill */}
      <div style={{
        background: `linear-gradient(135deg, ${PURPLE}, ${BLUE})`,
        padding: '14px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '32px', height: '32px' }} />
          <div>
            <div style={{ fontSize: '9px', opacity: 0.8 }}>Good evening</div>
            <div style={{ fontSize: '13px', fontWeight: '700' }}>Welcome 👋</div>
          </div>
        </div>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.18)',
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          border: '1px solid rgba(255,255,255,0.25)'
        }}>
          ₦948.50
        </div>
      </div>

      {/* Sport tabs */}
      <div style={{ display: 'flex', gap: '6px', padding: '10px 12px' }}>
        {[['🏀 NBA', true], ['⚽ EPL', false], ['⭐ More', false]].map(([label, active], i) => (
          <div key={i} style={{
            flex: 1,
            backgroundColor: active ? BLUE : '#1f2937',
            color: active ? '#fff' : '#9ca3af',
            textAlign: 'center',
            padding: '7px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: '600'
          }}>{label}</div>
        ))}
      </div>

      {/* Player prop cards - faithful 2x2 reproduction of the mobile prop card:
          matchup header, avatar + name/team, stat box with LINE, OVER/UNDER */}
      <div style={{ padding: '2px 10px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', flex: 1 }}>
        {[
          { initials: 'VW', name: 'V. Wembanyama', team: 'Spurs', match: 'Spurs vs Knicks', stat: 'POINTS', line: '21.5', pick: 'over' },
          { initials: 'KT', name: 'K-A. Towns', team: 'Knicks', match: 'Spurs vs Knicks', stat: 'REBOUNDS', line: '11.5', pick: null },
          { initials: 'MS', name: 'M. Salah', team: 'Liverpool', match: 'LIV vs MCI', stat: 'SHOTS', line: '2.5', pick: null },
          { initials: 'EH', name: 'E. Haaland', team: 'Man City', match: 'LIV vs MCI', stat: 'GOALS', line: '0.5', pick: 'over' },
        ].map((p, i) => (
          <div key={i} style={{
            background: 'linear-gradient(145deg, #1e293b, #111827)',
            borderRadius: '12px',
            border: '1px solid #334155',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Matchup header */}
            <div style={{
              background: 'linear-gradient(90deg, #0f172a, #1e293b)', padding: '5px 7px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontSize: '6px', color: '#cbd5e1', fontWeight: '600' }}>{p.match}</span>
              </div>
              <span style={{ fontSize: '6px', color: '#60a5fa' }}>8:40 PM</span>
            </div>

            <div style={{ padding: '7px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* Avatar + name + team */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: `linear-gradient(135deg, #6366f1, ${BLUE})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '8px', fontWeight: '700', flexShrink: 0
                }}>{p.initials}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '9px', fontWeight: '700', lineHeight: '11px' }}>{p.name}</div>
                  <div style={{ fontSize: '7px', color: '#94a3b8' }}>{p.team}</div>
                </div>
              </div>

              {/* Stat box with LINE label */}
              <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', border: '1px solid #334155', borderRadius: '7px', padding: '5px 7px' }}>
                <div style={{ fontSize: '6px', color: '#94a3b8', fontWeight: '700', letterSpacing: '0.5px' }}>{p.stat}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '800' }}>{p.line}</span>
                  <span style={{ fontSize: '6px', color: '#60a5fa', fontWeight: '600' }}>LINE</span>
                </div>
              </div>

              {/* OVER / UNDER */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                <div style={{
                  padding: '5px', borderRadius: '6px', textAlign: 'center', fontSize: '8px', fontWeight: '700',
                  background: p.pick === 'over' ? 'linear-gradient(135deg, #059669, #10b981)' : '#374151'
                }}>OVER</div>
                <div style={{ padding: '5px', borderRadius: '6px', textAlign: 'center', fontSize: '8px', fontWeight: '700', backgroundColor: '#374151' }}>UNDER</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom nav - matches mobile: Home / My Bets / Copilot / Profile */}
      <div style={{
        display: 'flex', justifyContent: 'space-around', padding: '10px 0',
        backgroundColor: '#0a0a0a', borderTop: '1px solid #1f2937'
      }}>
        {[
          { icon: <Home size={16} />, label: 'Home', color: '#3b82f6' },
          { icon: <Trophy size={16} />, label: 'My Bets', color: '#6b7280' },
          { icon: <Sparkles size={16} />, label: 'Playmaker', color: '#a78bfa' },
          { icon: <User size={16} />, label: 'Profile', color: '#6b7280' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', color: item.color }}>
            {item.icon}
            <div style={{ fontSize: '8px', fontWeight: '600' }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Watch Demo - an in-page interactive walkthrough of the betting flow.
// Auto-advances through the steps; users can also click the dots.
// ---------------------------------------------------------------------------
const DEMO_STEPS = [
  {
    title: 'Pick a player prop',
    body: 'Browse live basketball and football player props. Tap OVER or UNDER on a player’s stat line.',
    accent: BLUE,
    visual: (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ background: 'linear-gradient(145deg,#1e293b,#111827)', border: '1px solid #334155', borderRadius: '10px', padding: '10px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700' }}>Victor Wembanyama</div>
          <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '6px' }}>Points · 21.5</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
            <div style={{ padding: '6px', borderRadius: '6px', textAlign: 'center', fontSize: '9px', fontWeight: '700', background: 'linear-gradient(135deg,#059669,#10b981)' }}>OVER ✓</div>
            <div style={{ padding: '6px', borderRadius: '6px', textAlign: 'center', fontSize: '9px', backgroundColor: '#374151' }}>UNDER</div>
          </div>
        </div>
        <div style={{ background: 'linear-gradient(145deg,#1e293b,#111827)', border: '1px solid #334155', borderRadius: '10px', padding: '10px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700' }}>Mohamed Salah</div>
          <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '6px' }}>Shots · 2.5</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
            <div style={{ padding: '6px', borderRadius: '6px', textAlign: 'center', fontSize: '9px', fontWeight: '700', background: 'linear-gradient(135deg,#059669,#10b981)' }}>OVER ✓</div>
            <div style={{ padding: '6px', borderRadius: '6px', textAlign: 'center', fontSize: '9px', backgroundColor: '#374151' }}>UNDER</div>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Build your parlay',
    body: 'Stack 2 to 6 picks into one slip. The more legs you add, the bigger the multiplier.',
    accent: PURPLE,
    visual: (
      <div style={{ background: '#1f2937', borderRadius: '10px', padding: '12px', border: '1px solid #374151' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>Parlay Builder (3 Picks)</div>
        {['Wembanyama Over 21.5', 'Salah Over 2.5', 'Towns Under 11.5'].map((p, i) => (
          <div key={i} style={{ backgroundColor: '#374151', padding: '7px 8px', borderRadius: '6px', fontSize: '9px', marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>
            <span>{p}</span><span style={{ color: '#10b981' }}>✓</span>
          </div>
        ))}
      </div>
    )
  },
  {
    title: 'Safety Play or Beast Mode',
    body: 'Safety Play pays out even if one pick misses. Beast Mode is all-or-nothing for the biggest payout.',
    accent: '#10b981',
    visual: (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ background: 'rgba(16,185,129,0.12)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#10b981', marginBottom: '6px' }}>Safety Play ✓</div>
          <div style={{ fontSize: '9px', color: '#cbd5e1' }}>Partial payouts if most picks hit</div>
          <div style={{ fontSize: '16px', fontWeight: '900', color: '#10b981', marginTop: '8px' }}>2.5x</div>
        </div>
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid #7f1d1d', borderRadius: '12px', padding: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#ef4444', marginBottom: '6px' }}>Beast Mode</div>
          <div style={{ fontSize: '9px', color: '#cbd5e1' }}>All picks must hit</div>
          <div style={{ fontSize: '16px', fontWeight: '900', color: '#ef4444', marginTop: '8px' }}>6x</div>
        </div>
      </div>
    )
  },
  {
    title: 'Or let Playmaker build it',
    body: 'Tell our AI what you want — "two safe NBA picks under ₦200" — and Playmaker drafts a slip you can review and place.',
    accent: '#a78bfa',
    visual: (
      <div style={{ background: '#1f2937', borderRadius: '10px', padding: '12px', border: '1px solid #4c1d95' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <Sparkles size={14} color="#a78bfa" />
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#a78bfa' }}>Playmaker</span>
        </div>
        <div style={{ backgroundColor: '#312e81', padding: '8px', borderRadius: '8px', fontSize: '9px', marginBottom: '6px' }}>
          "Build me two safe NBA picks under ₦200"
        </div>
        <div style={{ backgroundColor: '#1e293b', padding: '8px', borderRadius: '8px', fontSize: '9px', color: '#cbd5e1' }}>
          Here's a Safety Play slip: Wembanyama Over 21.5 + Towns Under 11.5. Potential payout ₦450. Review and place 👇
        </div>
      </div>
    )
  }
];

const DemoModal = ({ onClose, onSignUp }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % DEMO_STEPS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const current = DEMO_STEPS[step];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '480px',
          background: 'linear-gradient(160deg, #111827, #0a0a0a)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px',
          padding: '28px', position: 'relative'
        }}
      >
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '10px', padding: '8px', cursor: 'pointer', color: 'white', display: 'flex' }}
        >
          <X size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '32px', height: '32px' }} />
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#94a3b8' }}>How PlayerBet works</span>
        </div>

        <div style={{
          display: 'inline-block', fontSize: '12px', fontWeight: '700',
          color: current.accent, background: `${current.accent}1a`,
          border: `1px solid ${current.accent}55`, padding: '4px 12px', borderRadius: '20px', marginBottom: '14px'
        }}>
          Step {step + 1} of {DEMO_STEPS.length}
        </div>

        <h3 style={{ fontSize: '24px', fontWeight: '800', margin: '0 0 8px' }}>{current.title}</h3>
        <p style={{ fontSize: '15px', color: '#94a3b8', lineHeight: '1.5', margin: '0 0 20px' }}>{current.body}</p>

        <div style={{ minHeight: '140px', marginBottom: '20px' }}>{current.visual}</div>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '20px' }}>
          {DEMO_STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              style={{
                width: i === step ? '24px' : '8px', height: '8px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                background: i === step ? current.accent : 'rgba(255,255,255,0.2)', transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>

        <button
          onClick={onSignUp}
          style={{
            width: '100%', background: `linear-gradient(135deg, ${PURPLE}, ${BLUE})`, color: 'white',
            border: 'none', borderRadius: '14px', padding: '16px', cursor: 'pointer', fontSize: '16px', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
          }}
        >
          Create your free account <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const LandingPage = ({ onLogin, onSignUp }) => {
  const [showDemo, setShowDemo] = useState(false);

  const features = [
    { title: 'Playmaker (AI)', description: 'Describe the bet you want in plain language and our AI drafts a slip you review before placing.', icon: <Sparkles size={28} color="#a78bfa" />, color: '#a78bfa' },
    { title: 'Beast Mode', description: 'All-or-nothing parlays with the biggest multipliers when every pick lands.', icon: '🔥', color: '#ef4444' },
    { title: 'Safety Play', description: 'Forgiving payouts — still win when most of your picks hit.', icon: <Shield size={28} color="#10b981" />, color: '#10b981' },
    { title: 'Live Player Props', description: 'Real-time over/under lines across basketball, football and more.', icon: '⚡', color: '#f59e0b' },
  ];

  const stats = [
    { label: 'Sports Markets', value: 'Live', icon: <Star size={22} /> },
    { label: 'Picks Per Slip', value: '2–6', icon: <TrendingUp size={22} /> },
    { label: 'AI Playmaker', value: 'Built-in', icon: <Sparkles size={22} /> },
    { label: 'Payouts', value: 'Instant', icon: <Shield size={22} /> },
  ];

  const btnPrimary = {
    background: `linear-gradient(135deg, ${PURPLE}, ${BLUE})`, color: 'white', border: 'none',
    borderRadius: '14px', cursor: 'pointer', fontWeight: '700', transition: 'all 0.3s ease',
    boxShadow: '0 8px 32px rgba(124,58,237,0.3)'
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: 'radial-gradient(circle at 20% 50%, rgba(124,58,237,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(37,99,235,0.12) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(16,185,129,0.05) 0%, transparent 50%)'
      }} />

      {/* Navigation */}
      <nav style={{
        position: 'relative', zIndex: 10, padding: '18px 40px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '44px', height: '44px' }} />
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, letterSpacing: '-0.5px' }}>PlayerBet</h1>
            <p style={{ fontSize: '11px', margin: 0, opacity: 0.8, color: '#60a5fa' }}>AI-Powered Player Props</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={onLogin} style={{
            background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '12px', padding: '11px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
          }}>Login</button>
          <button onClick={onSignUp} style={{ ...btnPrimary, padding: '11px 28px', fontSize: '14px' }}>Sign Up Free</button>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ position: 'relative', zIndex: 5, padding: '70px 40px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }}>
          {/* Left */}
          <div>
            <div style={{
              display: 'inline-block', background: 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(37,99,235,0.18))',
              padding: '8px 18px', borderRadius: '20px', marginBottom: '24px', border: '1px solid rgba(167,139,250,0.35)'
            }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#a78bfa', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={14} /> Now with Playmaker AI
              </span>
            </div>

            <h1 style={{
              fontSize: '54px', fontWeight: '900', lineHeight: '1.1', marginBottom: '22px', letterSpacing: '-2px',
              background: 'linear-gradient(135deg, #ffffff, #cbd5e1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
            }}>
              Player props,{' '}
              <span style={{ background: `linear-gradient(135deg, ${PURPLE}, ${BLUE}, #06b6d4)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                your way
              </span>
            </h1>

            <p style={{ fontSize: '19px', color: '#94a3b8', lineHeight: '1.6', marginBottom: '36px', maxWidth: '480px' }}>
              Build over/under player-prop parlays across basketball, football and more — or let Playmaker draft one for you. Play it safe or go all-in with Safety Play and Beast Mode.
            </p>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '40px', flexWrap: 'wrap' }}>
              <button onClick={onSignUp} style={{ ...btnPrimary, padding: '18px 36px', fontSize: '17px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                Start Betting Free <ArrowRight size={20} />
              </button>
              <button onClick={() => setShowDemo(true)} style={{
                background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '14px', padding: '18px 36px', cursor: 'pointer', fontSize: '17px', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                <Play size={18} /> Watch Demo
              </button>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {stats.map((stat, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
                  <div style={{ color: '#60a5fa', marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>{stat.icon}</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>{stat.value}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - phone */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <div style={{
              position: 'absolute', width: '320px', height: '320px', borderRadius: '50%',
              background: `radial-gradient(circle, ${PURPLE}33 0%, transparent 70%)`, filter: 'blur(40px)'
            }} />
            <div style={{ position: 'relative', zIndex: 2 }}>
              <PhoneMockup />
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div style={{
        position: 'relative', zIndex: 5,
        background: 'linear-gradient(180deg, transparent 0%, rgba(15,23,42,0.5) 50%, rgba(15,23,42,0.8) 100%)',
        padding: '90px 40px', backdropFilter: 'blur(20px)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '42px', fontWeight: '900', marginBottom: '16px', background: 'linear-gradient(135deg, #ffffff, #e2e8f0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Everything you get
          </h2>
          <p style={{ fontSize: '18px', color: '#94a3b8', marginBottom: '56px', maxWidth: '600px', margin: '0 auto 56px' }}>
            The same features on web and mobile — your account and bets stay in sync.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginBottom: '72px' }}>
            {features.map((feature, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)', padding: '28px', borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)', textAlign: 'left', transition: 'all 0.3s ease', cursor: 'default'
              }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 20px 50px ${feature.color}33`; e.currentTarget.style.borderColor = `${feature.color}66`; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <div style={{ fontSize: '40px', marginBottom: '16px', height: '40px', display: 'flex', alignItems: 'center' }}>{feature.icon}</div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '10px', color: feature.color }}>{feature.title}</h3>
                <p style={{ fontSize: '15px', color: '#94a3b8', lineHeight: '1.6' }}>{feature.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(37,99,235,0.12))',
            padding: '56px', borderRadius: '24px', border: '1px solid rgba(124,58,237,0.25)'
          }}>
            <h3 style={{ fontSize: '34px', fontWeight: '800', marginBottom: '14px' }}>Ready to build your first slip?</h3>
            <p style={{ fontSize: '17px', color: '#94a3b8', marginBottom: '30px' }}>
              Create a free account and place your first parlay in minutes.
            </p>
            <button onClick={onSignUp} style={{ ...btnPrimary, padding: '18px 44px', fontSize: '19px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              Get Started Free <ArrowRight size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ position: 'relative', zIndex: 5, borderTop: '1px solid rgba(255,255,255,0.1)', padding: '40px', textAlign: 'center', background: 'rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '16px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '32px', height: '32px' }} />
          <span style={{ fontSize: '18px', fontWeight: '700' }}>PlayerBet</span>
        </div>
        <div style={{
          display: 'inline-block', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '20px', padding: '6px 16px', marginBottom: '16px', fontSize: '13px', color: '#fca5a5', fontWeight: '600'
        }}>
          🔞 18+ only · Play responsibly
        </div>
        <p style={{ fontSize: '13px', color: '#64748b', maxWidth: '560px', margin: '0 auto 12px', lineHeight: '1.6' }}>
          Betting involves risk. Only stake what you can afford to lose. Odds are estimates and payouts are not guaranteed.
        </p>
        <p style={{ fontSize: '12px', color: '#475569' }}>© {new Date().getFullYear()} PlayerBet. All rights reserved.</p>
      </footer>

      {showDemo && <DemoModal onClose={() => setShowDemo(false)} onSignUp={onSignUp} />}
    </div>
  );
};

export default LandingPage;
